import { Component, Vue, Prop } from 'vue-property-decorator';
import { AttachmentPayload, Report } from '@/models/Report';
import FileToBase64 from '@/support/FileToBase64';
import StatelessUpload from '@/support/StatelessUpload';

@Component<FileUpload>({
  components: {},
  filters: {
    calculateKB: (value: number) => (value * 0.001).toFixed(1),
    concatenateName: (value: string) => {
      if (value.length >= 25) {
        return `${value.slice(0, 15)}...${value.slice(value.length - 10, value.length)}`;
      }
      return value;
    },
  },
})
export default class FileUpload extends Vue {
  @Prop()
  protected acceptedFileTypes?: string[];

  protected accepted: string [] = [];

  @Prop({ default: 300 })
  protected height?: number;

  @Prop()
  protected filePath?: string;

  @Prop()
  protected report?: Report;

  @Prop({ default: false })
  protected clearAfterUpload?: boolean;

  @Prop({ default: false })
  protected multiple?: boolean;

  @Prop({ default: 'mdi-plus' })
  protected fileIcon?: string;

  @Prop({ default: '' })
  protected description?: string;

  @Prop({ default: false })
  protected showToAll?: boolean;

  @Prop()
  protected isEvaluation?: boolean;

  @Prop({ default: '' })
  protected type?: string;

  @Prop({ default: '' })
  protected fileName?: string;

  @Prop({ default: false })
  protected kpiPoints?: boolean;

  @Prop({ default: null })
  protected amountOfKpiPoints?: number;

  //
  // STATES
  //
  protected dragging = false;

  protected hovering = false;

  protected isUploaded = false;

  protected hasError = false;

  protected invalidFileType = false;

  protected isSettingPayload = false;

  protected style: RippleStyle = {};

  // Payload, this value will be emitted when selecting or dropping files
  protected payload: AttachmentPayload[] = [];

  // Files
  protected files: File[] = [];

  //
  // TO TRACK IF A FILE IS UPLOADED OR NOT: MULTIPLE ONLY
  //
  protected filesUploaded: FilesUpload[] = [];

  //
  // PARSE CORRECT ICON FOR FILE TYPE
  //
  protected imageExtesions = ['jpg', 'jpeg', 'png', 'svg', 'gif', 'bmp'];

  protected fileIcons: {[key:string]:string} = {
    pdf: 'mdi-file-pdf-box',
    xls: 'mdi-file-excel-box',
    xlsx: 'mdi-file-excel-box',
    'vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'mdi-file-excel-box',
    docx: 'mdi-file-word-box',
    doc: 'mdi-file-word-box',
    'vnd.openxmlformats-officedocument.wordprocessingml.document': 'mdi-file-word-box',
    'vnd.ms-powerpoint': 'mdi-file-powerpoint-box',
    ppt: 'mdi-file-powerpoint-box',
    pptx: 'mdi-file-powerpoint-box',
    other: 'mdi-file-document-box',
    jpg: 'mdi-image',
    jpeg: 'mdi-image',
    png: 'mdi-image',
    svg: 'mdi-image',
  };

  protected mounted(): void {
    if (! this.acceptedFileTypes) {
      this.accepted = [
        'image/png',
        'image/jpeg',
        'image/svg+xml',
        'text/plain',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-powerpoint',
        'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/pdf',
        'msg',
        'oft',
      ];
    } else {
      this.accepted = this.acceptedFileTypes;
    }
  }

  protected getFileIcon(file: File): string {
    const type = file.type.split('/')[1];

    return this.fileIcons[type];
  }

  protected async setPayload(files: File[]): Promise<void> {
    const filesPromises = files.map(StatelessUpload.handle);
    const statelessUpload = await Promise.all(filesPromises);

    statelessUpload.forEach((response, index) => {
      if (! response.file) {
        return null;
      }

      this.filesUploaded.push({
        isLoading: true,
        isUploaded: false,
        icon: this.getFileIcon(response.file),
      });

      this.payload.push({
        description: this.description,
        hin_show_to_all: this.showToAll,
        type: this.type,
        kpi_points: this.kpiPoints,
        amount_of_kpi_points: this.amountOfKpiPoints,
        file: response.key,
        file_name: response.file.name,
      });

      this.isSettingPayload = true;

      setTimeout(() => {
        this.handleUploaded(index);
      }, 500);
    });
  }

  //
  // EVENTS
  //
  protected async onFileInputChange(): Promise<void> {
    if (this.isLoadingFiles) {
      return;
    }

    if (this.input.files) {
      this.files = Array.from(this.input.files);
      await this.setPayload(this.files);
      this.$emit('filesDropped', this.payload);
    }
  }

  protected handleMouseEnter(): void {
    this.hovering = true;
    this.style = {};
  }

  protected handleMouseLeave(): void {
    this.hovering = false;
  }

  protected handleClickUploaderAction(): void {
    if (this.isLoadingFiles) {
      return;
    }

    const input = this.$refs.hiddenFileInput as HTMLInputElement;
    if (input) {
      this.reset();
      input.value = '';
      const event = new MouseEvent('click');
      input.dispatchEvent(event);
    }
  }

  protected handleDragEnd(): void {
    this.dragging = false;
  }

  protected handleDragEnter(event: DragEvent): void {
    if (! event.dataTransfer) {
      return;
    }

    this.dragging = true;
    const element: HTMLElement = event.target as HTMLElement;
    this.setRippleStyle(element, event.pageX, event.pageY);

    if (event.dataTransfer.items && event.dataTransfer.items.length && this.accepted.length) {
      const file = Array.from(event.dataTransfer.items)[0];
      this.invalidFileType = ! this.accepted.includes(file.type.toString());
    }
  }

  protected handleDragOver(): void {
    this.dragging = true;
  }

  protected handleDragLeave(): void {
    this.reset();
  }

  protected async handleDrop(event: DragEvent): Promise<void> {
    if (this.invalidFileType) {
      this.reset();
      return;
    }

    this.dragging = false;
    if (! event.dataTransfer) {
      return;
    }

    this.files = Array.from(event.dataTransfer.files);

    await this.setPayload(this.files);
    this.$emit('filesDropped', this.payload);
  }

  //
  // PARSE FILE TYPE TO ICON & NAME
  //
  private get fileNameFromFilePath() {
    if (! this.filePath) {
      return '';
    }

    const parsedPath = this.filePath.split('/').pop();
    if (! parsedPath) {
      return '';
    }
    return parsedPath.split('?')[0];
  }

  private get fileExtension() {
    return this.fileNameFromFilePath ? this.fileNameFromFilePath.split('.').pop() : null;
  }

  protected get iconClass(): string {
    if (! this.fileExtension || ! this.fileIcons[this.fileExtension]) {
      return this.fileIcons.other;
    }

    return this.fileIcons[this.fileExtension];
  }

  protected get isImage(): boolean {
    if (! this.filePath) {
      return false;
    }

    if (! this.fileExtension) {
      return false;
    }

    return this.imageExtesions.includes(this.fileExtension) || this.filePath.includes('data:image');
  }

  protected get uploadMessage(): string {
    if (! this.dragging) {
      if (this.hovering) {
        return `Klik om één ${this.multiple ? 'of meer bestand(en)' : '​​bestand'} te selecteren`;
      }
      return `Versleep één ${this.multiple ? 'of meer bestand(en)' : '​​bestand'} om te uploaden`;
    }
    if (this.invalidFileType) {
      return `${this.multiple ? 'Één of meer bestand(en) worden' : 'Dit ​​bestand wordt'} niet ondersteund`;
    }
    return `Drop ${this.multiple ? 'één of meer bestand(en)' : 'dit bestand'} om te uploaden`;
  }

  protected get rippleStyle(): RippleStyle {
    return this.style;
  }

  private setRippleStyle(element: HTMLElement, x: number, y: number) {
    const boundingRectangle: ClientRect = element.getBoundingClientRect();
    const size = Math.max(boundingRectangle.width, boundingRectangle.height);
    this.style.height = `${size}px`;
    this.style.width = `${size}px`;
    this.style.top = `${y - boundingRectangle.top - size / 2}px`;
    this.style.left = `${x - boundingRectangle.left - size / 2}px`;
  }

  //
  // REMOVE FILE
  //
  protected removeFile(): void {
    this.$emit('fileRemoved');
    this.reset();
  }

  //
  // RESET UPLOAD
  //
  public reset(): void {
    this.dragging = false;
    this.hovering = false;
    this.isSettingPayload = false;
    this.invalidFileType = false;
    this.isUploaded = false;
    this.filesUploaded = [];
  }

  //
  // DONE UPLOADING
  //
  public handleUploaded(index: number): void {
    this.filesUploaded[index].isUploaded = true;
    this.filesUploaded[index].isLoading = false;
  }

  //
  // HANDLE ERROR
  //
  public handleError(index: number): void {
    this.filesUploaded[index].isUploaded = false;
    this.filesUploaded[index].isLoading = false;
  }

  //
  // GETTERS
  //
  protected get input(): HTMLInputElement {
    return this.$refs.hiddenFileInput as HTMLInputElement;
  }

  protected get isLoadingFiles(): boolean {
    return !! this.filesUploaded.length && !! this.filesUploaded[this.filesUploaded.length - 1].isLoading;
  }

  public uploadedMultiple(index: number): void {
    this.filesUploaded[index].isUploaded = true;
    this.filesUploaded[index].isLoading = false;
  }

  public uploadedMultipleFailed(index: number): void {
    this.filesUploaded[index].isUploaded = false;
    this.filesUploaded[index].isLoading = false;
  }
}

interface RippleStyle {
  top?: string;
  left?: string;
  height?: string;
  width?: string;
}

interface FilesUpload {
  isUploaded?: boolean;
  isLoading?: boolean;
  icon?: string;
}
